// M8.1 + M8.2 + M8.3 + M8.4 — AskPage (Hỏi đáp NV-facing UI).
//
// Map 1-1 với AC-QA-1..8 (PRD §7.10):
//   - AC-QA-1 auto_answer (top1 ≥ 0.92) — rich HTML + badge "Từ Bộ X — độ khớp N%" + "Xem N câu liên quan" (M8.3 ✓)
//   - AC-QA-2 candidate_list ([0.70, 0.92)) — 10 cards expand inline + yellow btn "Không câu nào đúng — Hỏi AI" (M8.3 ✓)
//   - AC-QA-3 no_match (<0.60) — skip list, modal "Đã kiểm tra — Tạo câu trả lời AI" (M8.2 ✓)
//   - AC-QA-4 Step 2 RAG — markdown + sources + badge "AI đã sinh — đã lưu Chờ duyệt" (M8.3 ✓)
//   - AC-QA-5 Cache 1h (BE lo)
//   - AC-QA-6 Feedback 👍/👎 → POST /ask/:id/feedback (M8.4 ✓)
//   - AC-QA-7 Bookmark + Copy:
//       · Bookmark: POST /bookmarks {question_id}   (hỗ trợ auto_answer matched_question, candidate card, Step 2 cho_duyet_id)
//       · Copy:     navigator.clipboard.writeText(plain text answer) — paste được sang Zalo/chat khách
//   - AC-QA-8 Analytics admin (M9+)
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useAuthStore } from '../lib/auth.js';
import { api } from '../lib/api.js';
import { renderRichText } from '../lib/renderRichText.js';
import { boShort } from '../lib/bo.js';

// ---------- Marked config (khớp ExamPage) ----------
marked.setOptions({ breaks: true, gfm: true });

// ---------- Sanitize helpers (copy từ ExamPage — sẽ extract lib/sanitize.js sau) ----------
const SAFE_TIPTAP_TAGS = [
  'p', 'br', 'strong', 'em', 'u', 's', 'code', 'pre',
  'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'hr', 'span', 'div',
];
const SAFE_TIPTAP_ATTR = ['class'];
function sanitizeRichHtml(html) {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: SAFE_TIPTAP_TAGS,
    ALLOWED_ATTR: SAFE_TIPTAP_ATTR,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'style', 'srcset', 'src'],
  });
}
function sanitizeMarkdownHtml(html) {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...SAFE_TIPTAP_TAGS, 'a', 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
    ALLOWED_ATTR: ['class', 'href', 'title'],
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'style', 'srcset', 'src'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  });
}

// Strip HTML → plain text. Dùng cho cả snippet (candidate_list) và clipboard copy (M8.4).
// maxLen=null nghĩa không cắt (copy full).
function stripHtmlToText(html, maxLen = 200) {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = sanitizeRichHtml(html);
  const text = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
  if (maxLen == null) return text;
  return text.length > maxLen ? text.slice(0, maxLen) + '…' : text;
}

// Strip MARKDOWN → plain text cho copy answer AI (Step 2). Bỏ cú pháp md (# ** - [] ...).
// Cách đơn giản & an toàn: parse md → html → stripHtml.
function stripMdToText(md) {
  if (!md) return '';
  try {
    return stripHtmlToText(marked.parse(String(md)), null);
  } catch {
    return String(md);
  }
}

// M21 v2 (2026-04-24) — Convert rich HTML → "pretty plain text" cho Zalo/Messenger.
// Parse HTML DOM → walk tree, trả text có cấu trúc bằng unicode thuần.
function htmlToPrettyPlain(html) {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = sanitizeRichHtml(html);

  function walk(node, depth = 0) {
    if (node.nodeType === 3) return node.textContent || ''; // text node
    if (node.nodeType !== 1) return '';
    const tag = node.tagName.toLowerCase();
    const childText = Array.from(node.childNodes).map((c) => walk(c, depth + 1)).join('');
    switch (tag) {
      case 'h1': case 'h2': case 'h3':
        return `\n\n${childText.toUpperCase().trim()}\n`;
      case 'h4': case 'h5': case 'h6':
        return `\n\n${childText.trim()}\n`;
      case 'p':     return `${childText.trim()}\n\n`;
      case 'br':    return '\n';
      case 'li':    return `• ${childText.trim()}\n`;
      case 'ul': case 'ol': return `${childText.trim()}\n\n`;
      case 'hr':    return '\n────────────────\n';
      case 'strong': case 'b': case 'em': case 'i':
        return childText; // Strip — Zalo không render bold/italic đẹp
      case 'a':
        const href = node.getAttribute('href') || '';
        return href && href !== childText ? `${childText} (${href})` : childText;
      default:      return childText;
    }
  }
  return walk(tmp).replace(/\n{3,}/g, '\n\n').trim();
}

// M21 v2 (2026-04-24) — Convert markdown → "pretty plain text" giữ cấu trúc cho Zalo/Messenger.
// Zalo/Messenger/Slack không hiểu HTML → text/plain phải format bằng ký tự unicode thuần.
// Input: `# Heading\n**bold**\n- item`
// Output: `HEADING\nbold\n• item`
function mdToPrettyPlain(md) {
  if (!md) return '';
  let s = String(md);
  // 1. Code blocks ``` — giữ nguyên content, bỏ fence
  s = s.replace(/```[\w]*\n([\s\S]*?)```/g, (_, code) => code.trim());
  // 2. Inline code `text` → text
  s = s.replace(/`([^`]+)`/g, '$1');
  // 3. Link [text](url) → text (url nếu không trùng text)
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, text, url) => (text === url ? text : `${text} (${url})`));
  // 4. Bold **text** hoặc __text__ → text (plain — Zalo không render bold đẹp)
  s = s.replace(/\*\*([^*\n]+)\*\*/g, '$1');
  s = s.replace(/__([^_\n]+)__/g, '$1');
  // 5. Italic *text* / _text_ → text
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1$2');
  s = s.replace(/(^|[^_])_([^_\n]+)_/g, '$1$2');
  // 6. Heading: # Title → UPPERCASE + blank line trước
  s = s.replace(/^#{1,6}\s+(.+)$/gm, (_, title) => `\n${title.toUpperCase()}\n`);
  // 7. Bullet list: - / * → • (ở đầu dòng, sau khoảng trắng)
  s = s.replace(/^(\s*)[-*]\s+/gm, '$1• ');
  // 8. Horizontal rule --- → ────────
  s = s.replace(/^[-*_]{3,}$/gm, '────────────────');
  // 9. Trim multi-blank-lines xuống 2
  s = s.replace(/\n{3,}/g, '\n\n');
  return s.trim();
}

// Clipboard helper — M21 (2026-04-24): dual format (html + plain text).
// Word/Gmail/Outlook pick text/html → giữ heading, bullet, bold. Editor thường pick text/plain.
// Fallback chain: ClipboardItem API → navigator.clipboard.writeText → document.execCommand('copy').
async function copyToClipboard(textOrObj) {
  // Backward-compat: caller truyền string → treat as plain text only
  const opts = typeof textOrObj === 'string' ? { text: textOrObj } : (textOrObj || {});
  const text = opts.text || '';
  const html = opts.html || '';
  if (!text && !html) throw new Error('Không có nội dung để copy');

  // Try 1: ClipboardItem với dual mime (best — Word giữ format)
  if (html && navigator.clipboard && window.ClipboardItem && window.isSecureContext) {
    try {
      const item = new ClipboardItem({
        'text/html':  new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text || html.replace(/<[^>]+>/g, '')], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([item]);
      return;
    } catch {
      /* fallthrough */
    }
  }

  // Try 2: writeText plain
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text || html);
    return;
  }

  // Try 3: fallback textarea + execCommand (http://localhost)
  const ta = document.createElement('textarea');
  ta.value = text || html;
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  try {
    const ok = document.execCommand('copy');
    if (!ok) throw new Error('document.execCommand copy thất bại');
  } finally {
    document.body.removeChild(ta);
  }
}

// M8.4-fix-K (2026-04-23): bỏ chọn bộ — BE luôn search toàn kho (trừ cho_duyet/bo_ai_*)
// vì NV không biết câu thuộc bộ nào & lúc nào cũng muốn tìm rộng nhất.
// (Trước đây có select "— Tất cả bộ —" nhưng default + 99% NV để mặc định → thừa UI.)

export default function AskPage() {
  const nav = useNavigate();
  const { user, l2Employee, getValidL2SessionId } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const hasValidL2 = !!getValidL2SessionId() && !!l2Employee;

  // ===== FORM =====
  const [question, setQuestion] = useState('');
  // M8.4-fix-K: bỏ state `bo` — luôn search toàn kho (bo=null). Xem comment BO_SEARCH_OPTIONS.
  const [loading, setLoading] = useState(null); // null | 'step1' | 'step2'

  // ===== RESULT =====
  // 2026-04-24 (M45): Chat-style history — mỗi lần search đẩy 1 entry vào sessions.
  // Câu cũ vẫn hiển thị (không bị overwrite khi NV hỏi câu mới).
  // Entry shape: { id, question, step1?, step2?, error? }
  const [sessions, setSessions] = useState([]);
  // Helpers: current = entry cuối cùng (đang được làm việc)
  const currentSession = sessions[sessions.length - 1] || null;
  const step1Result = currentSession?.step1 || null;
  const step2Result = currentSession?.step2 || null;
  const error = currentSession?.error || null;

  // Update current entry (entry cuối cùng trong sessions)
  function updateCurrentSession(patch) {
    setSessions(prev => prev.map((s, i) => i === prev.length - 1 ? { ...s, ...patch } : s));
  }

  // M45 + M46 (2026-04-25): auto-scroll khi có entry mới.
  // Newest nằm ở ĐẦU list (ngay dưới form) — cuộn lên top để NV thấy form + câu trả lời mới.
  useEffect(() => {
    if (sessions.length === 0) return;
    const t = setTimeout(() => {
      const latest = sessions[sessions.length - 1];
      const el = document.querySelector(`[data-session-id="${latest.id}"]`);
      if (el) {
        // Scroll sao cho entry newest hiện ngay dưới form, hơi có offset
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 50);
    return () => clearTimeout(t);
  }, [sessions.length]);

  // ===== MODAL =====
  const [showAiConfirm, setShowAiConfirm] = useState(false);

  // ===== M8.4 state =====
  // Set<number> các question_id NV đã bookmark (để Icon 🔖 render filled/outline).
  const [bookmarkedIds, setBookmarkedIds] = useState(() => new Set());
  // Map<historyId, 'up'|'down'> — cache feedback đã gửi (disable button sau khi bấm).
  const [feedbackByHistory, setFeedbackByHistory] = useState(() => new Map());

  // ----- guard L2 -----
  useEffect(() => {
    if (isAdmin) {
      toast('Admin không hỏi AI thay NV được — đăng nhập NV (L2) để dùng', { icon: '🚫', duration: 4000 });
      return;
    }
    if (!hasValidL2) {
      toast.error('Vui lòng xác minh L2 trước khi dùng Hỏi đáp');
      nav('/', { replace: true });
    }
  }, [isAdmin, hasValidL2, nav]);

  // Load bookmark ids 1 lần khi L2 valid — giúp render trạng thái 🔖 đúng.
  useEffect(() => {
    if (isAdmin || !hasValidL2) return;
    let cancelled = false;
    api.bookmarkList()
      .then((res) => {
        if (cancelled) return;
        const ids = new Set((res?.bookmarks || []).map((b) => b.question_id));
        setBookmarkedIds(ids);
      })
      .catch(() => {
        // Silent — bookmark UI sẽ hoạt động nhưng không biết trạng thái ban đầu.
      });
    return () => { cancelled = true; };
  }, [isAdmin, hasValidL2]);

  function onBookmarkChange(questionId, added) {
    setBookmarkedIds((prev) => {
      const next = new Set(prev);
      if (added) next.add(questionId); else next.delete(questionId);
      return next;
    });
  }

  function onFeedbackSent(historyId, feedback) {
    setFeedbackByHistory((prev) => {
      const next = new Map(prev);
      next.set(historyId, feedback);
      return next;
    });
  }

  // ===== HANDLERS =====
  async function onSearch(e) {
    e?.preventDefault();
    const q = question.trim();
    if (!q) return toast.error('Vui lòng nhập câu hỏi');
    if (q.length > 5000) return toast.error('Câu hỏi không được dài quá 5000 ký tự');

    // M45: push entry mới vào history (không reset cũ)
    const newEntry = {
      id: Date.now() + Math.random(),
      question: q,
      step1: null,
      step2: null,
      error: null,
    };
    setSessions(prev => [...prev, newEntry]);
    setShowAiConfirm(false);
    setLoading('step1');
    setQuestion('');  // Clear input để NV gõ câu tiếp theo

    try {
      const res = await api.askStep1(q, null);
      updateCurrentSession({ step1: res });
      if (res.case_type === 'no_match') setShowAiConfirm(true);
    } catch (err) {
      const head = err.data?.error || err.message || 'Tra cứu thất bại';
      const detail = err.data?.detail ? ` — ${err.data.detail}` : '';
      updateCurrentSession({ error: head + detail });
      toast.error(head);
    } finally {
      setLoading(null);
    }
  }

  function onOpenAiConfirm() { setShowAiConfirm(true); }
  function onCloseAiConfirm() { setShowAiConfirm(false); }

  async function onConfirmAskAi() {
    setShowAiConfirm(false);
    if (!step1Result?.history_id) {
      toast.error('Mất session Step 1, vui lòng tra cứu lại');
      return;
    }
    setLoading('step2');
    updateCurrentSession({ error: null });
    try {
      const res = await api.askStep2({
        history_id: step1Result.history_id,
        bo: null,
      });
      updateCurrentSession({ step2: res });
      toast.success('AI đã tạo câu trả lời');
    } catch (err) {
      const head = err.data?.error || err.message || 'Step 2 thất bại';
      const detail = err.data?.detail ? ` — ${err.data.detail}` : '';
      updateCurrentSession({ error: head + detail });
      toast.error(head);
    } finally {
      setLoading(null);
    }
  }

  // Clear toàn bộ lịch sử (nút "Xoá lịch sử" nếu cần)
  function clearHistory() {
    setSessions([]);
    setShowAiConfirm(false);
  }

  // Bundle action props truyền xuống để tránh prop-drilling lặp.
  const actionCtx = useMemo(() => ({
    bookmarkedIds,
    onBookmarkChange,
    feedbackByHistory,
    onFeedbackSent,
  }), [bookmarkedIds, feedbackByHistory]);

  return (
    <div className="min-h-full bg-gradient-to-br from-sky-50/60 via-white to-indigo-50/40 py-8 px-4 md:px-6">
      <div className="max-w-3xl mx-auto">
        {/* ===== Back button (trên grid) ===== */}
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-sky-700 transition mb-4"
          data-testid="ask-back"
        >
          <span>←</span> Quay lại
        </Link>

        {/* ===== HEADER card ===== */}
        <header className="mb-5 bg-white rounded-2xl border border-sky-100 shadow-sm p-5 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-500 text-white flex items-center justify-center text-2xl shadow-md shrink-0">
              🔍
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-slate-800">Hỏi đáp</h1>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                Tra cứu câu hỏi có sẵn · Nếu chưa có, AI sẽ tạo câu trả lời dựa trên tài liệu nội bộ
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* M126 (2026-04-27): Shortcut "Câu đã lưu" chuyển từ HomePage sang
                đây — NV ở trong Hỏi đáp dễ xem lại câu đã bookmark. */}
            <Link
              to="/bookmarks"
              data-testid="ask-bookmarks-shortcut"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-800 text-sm font-semibold border border-amber-200 transition shadow-sm"
              title="Xem các câu hỏi gần nhất bạn đã hỏi AI (tự động lưu 20 câu)"
            >
              <span>🔖</span>
              <span>Câu hỏi gần nhất hỏi</span>
            </Link>
            {l2Employee && (
              <div className="text-xs text-slate-500 text-right shrink-0 pl-3 border-l border-slate-100">
                <div className="uppercase tracking-wide text-[10px] font-semibold">Đang hỏi</div>
                <div className="font-bold text-slate-800 mt-0.5">{l2Employee.full_name}</div>
              </div>
            )}
          </div>
        </header>

        {/* ===== FORM — input hero ===== */}
        <form
          onSubmit={onSearch}
          className="bg-white rounded-3xl shadow-lg ring-1 ring-sky-100 p-6 md:p-7 mb-4"
        >
          <label
            className="flex items-center gap-1.5 text-sm font-bold text-slate-800 mb-3"
            htmlFor="ask-question"
          >
            <span className="text-base">💬</span>
            <span>Câu hỏi của bạn</span>
          </label>

          {/* M119 (2026-04-27): Hướng dẫn cách hỏi đầy đủ ngữ nghĩa.
              Lý do: NV hay gõ cụt ("Sữa tăng cân") → cosine retrieval kém,
              câu lưu lại trong "Chờ duyệt" cũng vô nghĩa cho lần sau. Card
              luôn hiện để training NV; nội dung ngắn gọn 2 cặp ví dụ ✅/❌. */}
          <div className="mb-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs leading-relaxed">
            <div className="font-bold text-amber-900 mb-2 flex items-start gap-1">
              <span>📌</span>
              <span>Hỏi đầy đủ ngữ nghĩa — đừng gõ cụt. Có tình huống phức tạp, dài thì hãy viết đầy đủ, rõ ràng.</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-slate-700">
              <div className="rounded-lg bg-white border border-emerald-200 px-2.5 py-2">
                <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 mb-1">✅ Đầy đủ</div>
                <div>"Khi tư vấn ghế ăn dặm cho khách cần hỏi thông tin gì?"</div>
                <div className="mt-1">"Khách hỏi tư vấn sữa tăng cân phải trả lời thế nào?"</div>
              </div>
              <div className="rounded-lg bg-white border border-rose-200 px-2.5 py-2">
                <div className="text-[10px] font-bold uppercase tracking-wide text-rose-700 mb-1">❌ Cụt lủn</div>
                <div className="line-through text-slate-400">"Ghế ăn dặm"</div>
                <div className="mt-1 line-through text-slate-400">"Sữa tăng cân"</div>
              </div>
            </div>
            <div className="mt-2 text-[11px] text-amber-800/90">
              💡 Có tình huống thì cứ viết đầy đủ. Sau khi hỏi AI mà vẫn chưa có đáp án — nhưng câu hỏi sẽ được lưu, và cập nhập thêm dữ liệu.
            </div>
          </div>

          <textarea
            id="ask-question"
            data-testid="ask-question-input"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              // Enter = submit, Shift+Enter = xuống dòng. Bỏ qua khi đang IME (Telex/VNI).
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                if (loading === null && question.trim()) {
                  onSearch(e);
                }
              }
            }}
            placeholder="VD: Khi tư vấn ghế ăn dặm cho khách cần hỏi thông tin gì?"
            className="w-full min-h-[110px] rounded-xl border-2 border-slate-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-100 focus:outline-none px-4 py-3 text-base leading-relaxed transition-all"
            disabled={loading !== null}
            maxLength={5000}
          />
          <div className="mt-4">
            <button
              type="submit"
              data-testid="ask-search-btn"
              disabled={loading !== null || !question.trim()}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-gradient-to-r from-sky-500 via-indigo-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700 disabled:from-slate-300 disabled:to-slate-300 disabled:cursor-not-allowed text-white text-base font-bold shadow-md hover:shadow-xl transition-all active:scale-[0.99]"
            >
              {loading === 'step1' ? (
                <>
                  <span className="animate-pulse">⏳</span>
                  <span>Đang tra cứu…</span>
                </>
              ) : (
                <>
                  <span className="text-lg">🔍</span>
                  <span>Tra cứu</span>
                  <kbd className="hidden md:inline-block ml-1 px-2 py-0.5 rounded bg-white/20 text-[10px] font-mono tracking-wider">Enter</kbd>
                </>
              )}
            </button>
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-slate-400 flex-wrap gap-1">
            <span className="tabular-nums">
              {question.length}<span className="text-slate-300">/5000 ký tự</span>
            </span>
            <span className="text-[11px]">
              💡 Gõ tiếng Việt có dấu để kết quả chính xác hơn · <kbd className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-mono">Shift+Enter</kbd> xuống dòng
            </span>
          </div>
        </form>

        {/* M45 (2026-04-24) + M46 (2026-04-25): Chat-style history — render entry MỚI NHẤT TRÊN CÙNG,
            entries cũ kéo xuống dưới làm tham khảo (giống giao diện Chat AI thông dụng).
            Mỗi entry = 1 câu hỏi + Step1 result + (nếu có) Step2 result.
            Câu cũ KHÔNG bị overwrite khi NV hỏi câu mới. */}
        {sessions.length > 0 && (
          <div className="mb-3 flex items-center justify-between text-xs text-slate-500">
            <span>
              💬 <b className="text-slate-700">{sessions.length}</b> câu đã hỏi trong phiên này
              {sessions.length > 1 && <span className="ml-1 text-slate-400">· Câu mới nhất ở trên cùng</span>}
            </span>
            <button
              onClick={clearHistory}
              className="text-slate-400 hover:text-rose-600 transition"
              title="Xoá lịch sử hỏi (không ảnh hưởng dữ liệu backend)"
            >
              🗑 Xoá lịch sử
            </button>
          </div>
        )}

        {/* M46: reverse — newest first. Divider ngăn cách giữa newest và các câu tham khảo cũ. */}
        {[...sessions].reverse().map((s, displayIdx) => {
          const originalIdx = sessions.length - 1 - displayIdx;
          const isLatest = displayIdx === 0;
          const showDivider = displayIdx === 1; // chèn divider ngay trước câu cũ đầu tiên
          return (
            <Fragment key={s.id}>
              {showDivider && (
                <div className="my-4 flex items-center gap-3 text-[11px] uppercase tracking-wider text-slate-400">
                  <span className="flex-1 h-px bg-slate-200" />
                  <span className="shrink-0">📖 Các câu đã hỏi trước đó · tham khảo</span>
                  <span className="flex-1 h-px bg-slate-200" />
                </div>
              )}
              <SessionEntry
                session={s}
                index={originalIdx}
                total={sessions.length}
                isLatest={isLatest}
                isLoading={isLatest && loading !== null}
                loadingPhase={isLatest ? loading : null}
                onOpenAiConfirm={isLatest ? onOpenAiConfirm : null}
                actionCtx={actionCtx}
              />
            </Fragment>
          );
        })}
      </div>

      {/* ===== AI CONFIRM MODAL ===== */}
      {showAiConfirm && (
        <AiConfirmModal
          caseType={step1Result?.case_type}
          question={currentSession?.question || ''}
          onCancel={onCloseAiConfirm}
          onConfirm={onConfirmAskAi}
        />
      )}
    </div>
  );
}

// =====================================================================
// Step 1 Panel — 3 branches theo case_type
// =====================================================================
// M45: SessionEntry — wrap 1 entry trong chat history
function SessionEntry({ session, index, total, isLatest, isLoading, loadingPhase, onOpenAiConfirm, actionCtx }) {
  const { question, step1, step2, error } = session;
  const showStep1 = !!step1 && !step2;
  const showStep2 = !!step2;
  const isOld = !isLatest;  // past entry — có thể collapse nếu muốn
  // Có thể thu gọn entry cũ, nhưng để NV đọc được luôn thì giữ mở

  return (
    <div className={`mb-4 ${isOld ? 'opacity-90' : ''}`} data-session-id={session.id}>
      {/* Question bubble — style chat */}
      <div className="mb-2 flex items-start gap-2">
        <div className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-full bg-gradient-to-br from-sky-500 to-indigo-500 text-white text-xs font-bold flex items-center justify-center shadow">
          {index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">
            Câu hỏi {index + 1} / {total}
            {isLatest && <span className="ml-1 text-sky-600 font-semibold">· Mới nhất</span>}
          </div>
          <div className="text-sm font-semibold text-slate-800 bg-sky-50 border border-sky-100 rounded-2xl rounded-tl-none px-4 py-2 inline-block max-w-full whitespace-pre-wrap break-words">
            {question}
          </div>
        </div>
      </div>

      {/* Loading bar (chỉ entry mới nhất đang load) */}
      {isLoading && loadingPhase === 'step1' && (
        <div className="mb-2 p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-600">
          🔍 Đang tra cứu kho câu hỏi…
        </div>
      )}
      {isLoading && loadingPhase === 'step2' && (
        <div className="mb-2 p-3 rounded-lg bg-sky-50 border border-sky-200 text-sm text-sky-700" data-testid="ask-step2-loading">
          🤖 Đang tạo câu trả lời bằng AI (5–15 giây)…
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700" data-testid="ask-error">
          ⚠ {error}
        </div>
      )}

      {/* Step1 panel */}
      {showStep1 && (
        <Step1Panel
          result={step1}
          onOpenAiConfirm={onOpenAiConfirm}
          actionCtx={actionCtx}
        />
      )}

      {/* Step2 panel */}
      {showStep2 && (
        <Step2Panel
          result={step2}
          originalQuestion={question}
          actionCtx={actionCtx}
        />
      )}
    </div>
  );
}

function Step1Panel({ result, onOpenAiConfirm, actionCtx }) {
  const { case_type } = result;

  // M8.4-fix-G (2026-04-23): BE giờ trả case_type GỐC kèm `cached` flag thay vì
  // luôn 'cached'. Trước đây include cached chung với auto_answer → lần 2 hỏi
  // câu match 89% (candidate_list gốc) bị render như auto_answer — UX đảo lộn.
  if (case_type === 'auto_answer') {
    // M8.4-fix-F: pass onOpenAiConfirm để fallback "Hỏi AI tạo câu mới".
    return <AutoAnswerView result={result} onOpenAiConfirm={onOpenAiConfirm} actionCtx={actionCtx} />;
  }
  if (case_type === 'candidate_list') {
    return <CandidateListView result={result} onOpenAiConfirm={onOpenAiConfirm} actionCtx={actionCtx} />;
  }
  if (case_type === 'no_match') {
    return (
      <div
        className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-4 text-center"
        data-testid="ask-step1-panel-no_match"
      >
        <div className="text-4xl mb-2 select-none">🤔</div>
        <div className="text-sm font-semibold text-slate-800">
          Chưa tìm thấy câu hỏi nào phù hợp
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Độ khớp tốt nhất: <strong>{((result.top1_score ?? 0) * 100).toFixed(1)}%</strong> (cần ≥{((result.thresholds?.candidate ?? 0.7) * 100).toFixed(0)}% để gợi ý)
          <br />Hộp thoại xác nhận tạo câu trả lời AI đang mở…
        </p>
      </div>
    );
  }
  // fallback (never happen nhưng defensive)
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-4 text-xs text-slate-500">
      case_type lạ: <code>{case_type}</code>
    </div>
  );
}

// ---------- AC-QA-1: auto_answer / cached ----------
function AutoAnswerView({ result, onOpenAiConfirm, actionCtx }) {
  const { matched_question, returned_answer, top1_score, top_candidates, cached, latency_ms, history_id } = result;
  const [showRelated, setShowRelated] = useState(false);
  const mq = matched_question;
  const answerHtml = sanitizeRichHtml(returned_answer || mq?.answer || '');
  // top_candidates cho auto_answer = top 3 (BE đã slice). Loại matched ra để tránh duplicate.
  const related = (top_candidates || []).filter((c) => c.id !== mq?.id);
  // M21 v2 (2026-04-24): plain text cho Zalo/Messenger — bullet + heading đẹp.
  // Auto answer nguồn là HTML (admin nhập rich text) → strip HTML + convert <li>/<h*> thành • / CAPS.
  const plainAnswer = htmlToPrettyPlain(returned_answer || mq?.answer || '');

  const scorePct = Math.round((top1_score || 0) * 100);

  return (
    <div
      className="bg-white rounded-3xl shadow-md ring-1 ring-emerald-100 overflow-hidden mb-4"
      data-testid="ask-step1-panel-auto_answer"
    >
      {/* Top strip gradient emerald */}
      <div className="h-1.5 bg-gradient-to-r from-emerald-400 via-teal-400 to-sky-400" />

      <div className="p-5 md:p-6">
        {/* Header row: badge trái + meta phải */}
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-sm">
            <span>✓</span>
            <span>Đã có sẵn</span>
            {cached && <span className="opacity-80 font-normal">· cache</span>}
          </span>
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            <span>Từ <strong className="text-slate-700">{boShort(mq?.bo)}</strong></span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 font-semibold">
              {scorePct}% khớp
            </span>
            <span className="text-slate-400">{latency_ms}ms</span>
          </div>
        </div>

        {/* Question — M181: renderRichText để gỡ wrapper <p>..</p> của M60 rich-text */}
        <div
          className="text-base font-bold text-slate-800 leading-relaxed mb-1 prose prose-sm max-w-none prose-p:my-0"
          dangerouslySetInnerHTML={{ __html: renderRichText(mq?.question || '') }}
        />
        {mq?.topic && (
          <div className="text-xs text-slate-500 mb-4">
            Chủ đề: <span className="font-medium text-slate-700">{mq.topic}</span>
          </div>
        )}

        {/* Answer block */}
        {answerHtml ? (
          <div className="relative mt-3">
            <div className="absolute -top-2.5 left-4 bg-white px-2 text-[10px] uppercase tracking-wide text-emerald-600 font-bold">
              Đáp án
            </div>
            <div
              className="markdown-body bg-gradient-to-br from-emerald-50/60 to-teal-50/40 rounded-2xl p-5 border border-emerald-100 text-slate-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: answerHtml }}
            />
          </div>
        ) : (
          <div className="rounded-2xl p-4 border-2 border-dashed border-amber-300 bg-amber-50 text-sm text-amber-800">
            <div className="font-semibold mb-1">⚠ Câu này trong kho nhưng chưa có đáp án</div>
            <div className="text-xs text-amber-700/90">
              Admin chưa nhập đáp án cho câu <em>"{stripHtmlToText(mq?.question || 'này', null)}"</em>.
              Báo admin bổ sung đáp án, hoặc hỏi lại câu khác.
            </div>
          </div>
        )}

        {/* Action row: feedback + bookmark + copy */}
        <ActionRow
          historyId={history_id}
          questionId={mq?.id}
          copyText={plainAnswer}
          copyHtml={answerHtml}
          actionCtx={actionCtx}
          testIdPrefix="ask-auto"
        />

        {/* Fallback AI — hiện khi match cao nhưng NV muốn đáp án mới */}
        {onOpenAiConfirm && (
          <div className="mt-3">
            <button
              type="button"
              onClick={onOpenAiConfirm}
              data-testid="ask-auto-fallback-ai-btn"
              className="w-full px-4 py-2.5 rounded-xl border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 text-amber-800 text-sm font-semibold flex items-center justify-center gap-2 shadow-sm hover:shadow transition-all"
              title="Dùng AI tạo câu trả lời mới dựa trên tài liệu nội bộ"
            >
              <span className="text-lg">🤖</span>
              <span>Không đúng ý? Yêu cầu AI tạo câu trả lời mới</span>
            </button>
          </div>
        )}

        {/* Related questions */}
        {related.length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-100">
            <button
              onClick={() => setShowRelated((v) => !v)}
              data-testid="ask-show-related-btn"
              className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-700 font-semibold transition-colors"
            >
              <span className={`transition-transform inline-block ${showRelated ? 'rotate-90' : ''}`}>▸</span>
              {showRelated ? 'Ẩn' : 'Xem'} <b>{related.length}</b> câu liên quan khác
            </button>
            {showRelated && (
              <ul className="mt-3 space-y-2">
                {related.map((c) => (
                  <CandidateRow key={c.id} c={c} historyId={result.history_id} compact actionCtx={actionCtx} />
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- AC-QA-2: candidate_list ----------
function CandidateListView({ result, onOpenAiConfirm, actionCtx }) {
  const { top_candidates, top1_score, latency_ms, history_id, thresholds, cached } = result;
  const list = top_candidates || [];

  return (
    <div
      className="bg-white rounded-3xl shadow-md ring-1 ring-amber-100 overflow-hidden mb-4"
      data-testid="ask-step1-panel-candidate_list"
    >
      <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" />

      <div className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-sm">
            <span>🔎</span>
            <span>Có {list.length} câu gần giống</span>
            {cached && <span className="opacity-80 font-normal">· cache</span>}
          </span>
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100 font-semibold">
              Khớp cao nhất {Math.round((top1_score || 0) * 100)}%
            </span>
            {thresholds?.auto_answer && (
              <span className="text-slate-400">
                cần ≥{Math.round(thresholds.auto_answer * 100)}% để khớp cứng
              </span>
            )}
            <span className="text-slate-400">{latency_ms}ms</span>
          </div>
        </div>

        <p className="text-sm text-slate-600 mb-4 leading-relaxed">
          {/* M116 (2026-04-27): bỏ "Bấm vào câu để xem đáp án" — answers giờ
              hiển thị sẵn không cần click. */}
          💡 Kiểm tra xem có câu nào giống câu hỏi của bạn không? Đáp án hiển thị sẵn ngay bên dưới.
        </p>

        <ul className="space-y-2">
          {list.map((c) => (
            <CandidateRow key={c.id} c={c} historyId={history_id} actionCtx={actionCtx} />
          ))}
        </ul>

        <button
          onClick={onOpenAiConfirm}
          data-testid="ask-fallback-ai-btn"
          className="mt-4 w-full px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm font-bold shadow-md hover:shadow-lg flex items-center justify-center gap-2 transition-all active:scale-[0.99]"
        >
          <span className="text-lg">🤖</span>
          <span>Không câu nào đúng — Yêu cầu AI tạo câu trả lời mới</span>
        </button>
      </div>
    </div>
  );
}

// ---------- Candidate card (dùng cho cả candidate_list và auto_answer related) ----------
// M116 (2026-04-27) — Rewrite UX: bỏ accordion click-expand, luôn render Q+A
// inline với 2 khối phân biệt màu rõ:
//   - Khối Câu hỏi: nền sky/blue (📝)
//   - Khối Câu trả lời: nền emerald/green (💬)
// Nguồn answer: BE candidateSummary giờ kèm `c.answer` (M116 BE) → không cần
// fetch questionGet/card. Trường hợp answer rỗng vẫn cảnh báo "admin chưa nhập".
// askClick log analytics: fire 1 lần khi card render (vì NV thấy luôn nội dung).
function CandidateRow({ c, historyId, compact = false, actionCtx }) {
  const clickedRef = useRef(false);

  useEffect(() => {
    if (clickedRef.current || !historyId) return;
    clickedRef.current = true;
    api.askClick(historyId, c.id).catch(() => {});
  }, [historyId, c.id]);

  const scorePct = (c.score * 100).toFixed(0);
  // M118 (2026-04-27): dùng renderRichText (giống admin /admin/questions list)
  // thay sanitizeRichHtml. Lý do: c.answer có thể là HTML (TipTap), markdown
  // (AI generate), hoặc plain text với "•" tự gõ. sanitizeRichHtml chỉ xử lý
  // case HTML → plain text với "•" hiển thị inline (UX bug). renderRichText
  // auto-detect: HTML → sanitize, markdown → marked.parse, plain → <p>+nl2br.
  const answerHtml = c.answer ? renderRichText(c.answer) : '';
  const answerPlain = c.answer ? stripHtmlToText(c.answer, null) : '';
  const hasAnswer = (c.answer || '').trim().length > 0;

  return (
    <li
      className="border border-slate-200 rounded-xl bg-white overflow-hidden shadow-sm"
      data-testid={`ask-candidate-${c.id}`}
    >
      {/* Header: score badge */}
      <div className="flex items-center justify-end px-3 pt-2">
        <span className={`text-[11px] font-mono font-bold px-2 py-0.5 rounded-full ${
          c.score >= 0.85 ? 'bg-emerald-100 text-emerald-700' :
          c.score >= 0.75 ? 'bg-amber-100 text-amber-700' :
          'bg-slate-100 text-slate-600'
        }`}>
          Khớp {scorePct}%
        </span>
      </div>

      {/* Khối Câu hỏi — nền sky */}
      <div className="mx-3 mt-1 mb-2 rounded-lg bg-sky-50 border border-sky-100 px-3 py-2">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-sky-700 mb-1">
          <span>📝</span>
          <span>Câu hỏi</span>
        </div>
        <div
          className={`text-sm text-slate-800 ${compact ? 'font-normal' : 'font-medium'} leading-relaxed prose prose-sm max-w-none prose-p:my-0`}
          dangerouslySetInnerHTML={{ __html: renderRichText(c.question || '') }}
        />
      </div>

      {/* Khối Câu trả lời — nền emerald (hoặc warning nếu trống) */}
      {hasAnswer ? (
        <div className="mx-3 mb-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-emerald-700 mb-1">
            <span>💬</span>
            <span>Câu trả lời</span>
          </div>
          <div
            className="markdown-body text-sm text-slate-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: answerHtml }}
          />
        </div>
      ) : (
        <div className="mx-3 mb-2 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-3 py-2">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-amber-700 mb-1">
            <span>⚠️</span>
            <span>Chưa có đáp án</span>
          </div>
          <div className="text-xs text-amber-800/90 leading-relaxed">
            Admin chưa nhập đáp án cho câu hỏi này. Bạn có thể bấm
            <strong> "Không câu nào đúng — Yêu cầu AI tạo câu trả lời mới" </strong>
            ở dưới cùng, hoặc báo admin bổ sung.
          </div>
        </div>
      )}

      {/* Action row: Lưu + Copy */}
      <div className="px-3 pb-3 pt-1 border-t border-slate-100">
        <ActionRow
          historyId={null}
          questionId={c.id}
          copyText={answerPlain}
          actionCtx={actionCtx}
          testIdPrefix={`ask-cand-${c.id}`}
        />
      </div>
    </li>
  );
}

// =====================================================================
// Step 2 Panel — AC-QA-4 RAG answer
// =====================================================================
function Step2Panel({ result, originalQuestion, actionCtx }) {
  const { answer, sources, related_questions, cho_duyet_id, cost_vnd, latency_ms, provider, model, history_id } = result;
  const mdHtml = answer ? sanitizeMarkdownHtml(marked.parse(answer)) : '';
  const plainAnswer = stripMdToText(answer);

  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-sky-200 p-5 mb-4"
      data-testid="ask-step2-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-sky-100 text-sky-800 border border-sky-200">
          🤖 AI đã sinh câu trả lời
        </span>
        {/* M45 (2026-04-25): NV KHÔNG cần thấy chi phí — chỉ admin xem ở /admin/cost.
            Giữ latency để NV thấy tốc độ response. */}
        <span className="text-xs text-slate-400">
          {latency_ms}ms
        </span>
      </div>

      {/* M55 (2026-04-25): bỏ echo "Câu hỏi: ..." — đã có bubble câu hỏi
          chat-style phía trên SessionEntry nên hiển thị ở đây sẽ redundant. */}

      {mdHtml
        ? <div
            className="markdown-body"
            dangerouslySetInnerHTML={{ __html: mdHtml }}
          />
        : <div className="text-sm text-slate-400 italic">Không có câu trả lời</div>
      }

      {/* M127 (2026-04-27): Bỏ "Câu hỏi liên quan" trong Step 2 panel — NV
          chỉ cần đáp án AI, danh sách câu liên quan gây nhiễu (đã có
          candidate_list ở Step 1 cho mục đích tra cứu giống).
          Giữ component RelatedQuestionsBlock + field BE `related_questions`
          phòng dùng lại sau, chỉ ẩn render. */}

      {/* Sources */}
      {Array.isArray(sources) && sources.length > 0 && <SourcesList sources={sources} />}

      {/* Cho duyet badge */}
      {cho_duyet_id && (
        <div className="mt-4 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center gap-2">
          <span>✓</span>
          <span>
            Câu hỏi + câu trả lời đã được lưu vào <strong>Chờ duyệt</strong> (#{cho_duyet_id}) để admin kiểm tra và phê duyệt.
          </span>
        </div>
      )}

      {/* ===== M8.4 action row ===== */}
      {/* M129 (2026-04-27): hideBookmark=true vì Step 2 đã auto-saved bởi M128 */}
      <ActionRow
        historyId={history_id}
        questionId={cho_duyet_id}         // bookmark trỏ vào question chờ duyệt — tra lại sau khi admin đổi bộ
        hideBookmark
        copyText={mdToPrettyPlain(answer)} // M21 v2: pretty plain — Zalo/Messenger hiển thị có cấu trúc
        copyHtml={mdHtml}                  // rendered HTML cho Word/Gmail paste đẹp
        actionCtx={actionCtx}
        testIdPrefix="ask-step2"
      />
    </div>
  );
}

// R-P1-9: Câu hỏi liên quan — top 3 từ cosine similarity.
// Click để expand xem đáp án (fetch qua api.questionGet).
function RelatedQuestionsBlock({ items }) {
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null); // { id, question, answer, bo, topic }

  async function toggle(id) {
    if (expandedId === id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(id);
    setDetail(null);
    setLoading(true);
    try {
      const r = await api.questionGet(id);
      // questionGet có thể trả { data: {...} } hoặc {...} — unwrap
      const q = r?.data || r?.question || r;
      setDetail(q);
    } catch (err) {
      setDetail({ error: String(err.message || err) });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mt-5 pt-4 border-t border-slate-100" data-testid="related-questions">
      <div className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
        <span>🔗</span>
        <span>Câu hỏi liên quan</span>
        <span className="text-xs font-normal text-slate-400">— click để xem đáp án</span>
      </div>
      <ul className="space-y-2">
        {items.map((q) => {
          const open = expandedId === q.id;
          return (
            <li
              key={q.id}
              className={`rounded-lg border ${open ? 'border-brand-300 bg-brand-50/40' : 'border-slate-200 bg-white'}`}
            >
              <button
                onClick={() => toggle(q.id)}
                className="w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-slate-50 rounded-lg"
              >
                <span className="text-slate-400 mt-0.5">{open ? '▼' : '▶'}</span>
                <span className="flex-1 text-sm text-slate-700 rich-content"
                      dangerouslySetInnerHTML={{
                        __html: renderRichText(q.question) +
                          (q.topic ? ` <span class="ml-2 text-xs text-slate-400">(${q.topic})</span>` : ''),
                      }} />
                <span className="text-xs font-mono text-slate-400 shrink-0">
                  {(q.score * 100).toFixed(0)}%
                </span>
              </button>
              {open && (
                <div className="px-3 pb-3 pt-1 text-sm">
                  {loading && <div className="text-slate-400 italic">Đang tải đáp án…</div>}
                  {detail?.error && (
                    <div className="text-red-600 text-xs">Lỗi: {detail.error}</div>
                  )}
                  {detail && !detail.error && (
                    <div
                      className="markdown-body bg-white rounded p-3 border border-slate-100"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichHtml(detail.answer || '<em class="text-slate-400">(Chưa có đáp án)</em>'),
                      }}
                    />
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Group sources theo loại để user dễ scan.
function SourcesList({ sources }) {
  const groups = {};
  for (const s of sources) {
    const k = s.type || s.source || 'khác';
    (groups[k] ||= []).push(s);
  }
  const keys = Object.keys(groups);
  const TYPE_LABEL = {
    question: 'Câu hỏi trong kho',
    knowledge: 'Tài liệu nội bộ',
    rule: 'Nội quy',
    prompt: 'Prompt mẫu',
    khác: 'Nguồn khác',
  };
  return (
    <div className="mt-4 pt-3 border-t border-slate-100">
      <div className="text-xs font-semibold text-slate-600 mb-2">
        📚 Nguồn tham khảo ({sources.length})
      </div>
      <div className="space-y-2">
        {keys.map((k) => (
          <div key={k}>
            <div className="text-[11px] uppercase text-slate-400 tracking-wide mb-1">
              {TYPE_LABEL[k] || k}
            </div>
            <ul className="text-xs text-slate-600 space-y-1 pl-3">
              {groups[k].map((s, i) => (
                <li key={i}>
                  • {s.title || s.question || s.topic || s.excerpt?.slice?.(0, 80) || JSON.stringify(s).slice(0, 80)}
                  {s.score != null && (
                    <span className="ml-2 text-slate-400 font-mono">{(s.score * 100).toFixed(0)}%</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// =====================================================================
// Modal cảnh báo "Đã kiểm tra — Tạo câu trả lời AI" — AC-QA-3 / AC-QA-2
// M130 (2026-04-27): nút "Kiểm tra lại" giờ điều hướng sang /bookmarks
// (Câu hỏi gần nhất hỏi) — NV có thể đã hỏi câu này rồi và quên đáp án,
// xem lại nhanh thay vì tốn phí gọi AI mới.
// =====================================================================
function AiConfirmModal({ caseType, question, onCancel, onConfirm }) {
  const nav = useNavigate();
  const headline = caseType === 'no_match'
    ? 'Chưa có câu hỏi sẵn phù hợp'
    : 'Đã kiểm tra kỹ các câu trên?';

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="ask-ai-confirm-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="text-3xl select-none">🤖</div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-slate-800">{headline}</h2>
            <p className="mt-1 text-sm text-slate-600 leading-relaxed">
              {/* M130 (2026-04-27): cảnh báo phí + gợi ý vào "Câu hỏi gần nhất hỏi"
                  trước khi tốn phí gọi AI mới. */}
              Bạn sắp dùng AI để tạo câu trả lời mới. Việc này mất khoảng 5–15 giây.
              <strong> Việc tạo câu trả lời sẽ mất phí</strong>, hãy kiểm tra lại nội dung câu trả lời sẵn.
              Nếu bạn vừa hỏi câu này bằng AI nhưng quên nội dung, hãy vào mục
              {' '}<strong>"Câu hỏi gần nhất hỏi"</strong>.
            </p>
            <div className="mt-3 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
              <strong>Câu hỏi:</strong> {question.length > 200 ? question.slice(0, 200) + '…' : question}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={() => { onCancel?.(); nav('/bookmarks'); }}
            data-testid="ask-ai-cancel"
            className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium"
            title="Đi tới Câu hỏi gần nhất hỏi để xem lại đáp án cũ"
          >
            🔖 Kiểm tra lại
          </button>
          <button
            onClick={onConfirm}
            data-testid="ask-ai-confirm"
            className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold shadow-sm"
          >
            Đã kiểm tra — Tạo câu trả lời AI
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// M8.4 — ActionRow: 👍 👎 🔖 📋
// =====================================================================
// historyId (optional): ai_ask_history.id → bật feedback 👍/👎
// questionId (optional): questions.id → bật bookmark 🔖 (cần có vì schema bookmark chỉ reference question)
// copyText: plain-text (markdown) để copy vào clipboard
// copyHtml (optional, M21): rendered HTML — paste vào Word/Gmail giữ heading+bullet
function ActionRow({ historyId, questionId, copyText, copyHtml, actionCtx, testIdPrefix, hideBookmark = false }) {
  const {
    bookmarkedIds,
    onBookmarkChange,
    feedbackByHistory,
    onFeedbackSent,
  } = actionCtx || {};

  const isBookmarked = questionId ? bookmarkedIds?.has(questionId) : false;
  const feedback = historyId ? feedbackByHistory?.get(historyId) : null;

  const showFeedback = !!historyId;
  // M129 (2026-04-27): hideBookmark=true cho Step 2 — câu AI đã auto-saved (M128),
  // nút "Lưu" thừa với NV. Step 1 candidate vẫn giữ nút (manual save từ kho cũ).
  const showBookmark = !!questionId && !hideBookmark;
  const showCopy = !!copyText;

  if (!showFeedback && !showBookmark && !showCopy) return null;

  return (
    <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
      {showFeedback && (
        <FeedbackButtons
          historyId={historyId}
          current={feedback}
          onSent={onFeedbackSent}
          testIdPrefix={testIdPrefix}
        />
      )}
      {showBookmark && (
        <BookmarkBtn
          questionId={questionId}
          isBookmarked={isBookmarked}
          onChange={onBookmarkChange}
          testId={`${testIdPrefix}-bookmark`}
        />
      )}
      {showCopy && (
        <CopyBtn text={copyText} html={copyHtml} testId={`${testIdPrefix}-copy`} />
      )}
    </div>
  );
}

function FeedbackButtons({ historyId, current, onSent, testIdPrefix }) {
  const [busy, setBusy] = useState(false);
  async function send(value) {
    if (busy || current) return; // 1 feedback per history — BE cũng idempotent nhưng UI chặn cho gọn
    setBusy(true);
    try {
      await api.askFeedback(historyId, value);
      onSent?.(historyId, value);
      toast.success(value === 'up' ? 'Cảm ơn, đã ghi nhận 👍' : 'Đã ghi nhận 👎 — admin sẽ review');
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Gửi feedback thất bại');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <button
        type="button"
        disabled={busy || !!current}
        onClick={() => send('up')}
        data-testid={`${testIdPrefix}-fb-up`}
        title="Câu trả lời này đúng"
        className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium border transition ${
          current === 'up'
            ? 'bg-emerald-100 text-emerald-800 border-emerald-300 cursor-default'
            : current
              ? 'bg-white text-slate-300 border-slate-200 cursor-not-allowed'
              : 'bg-white text-slate-600 border-slate-200 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200'
        }`}
      >
        <span>👍</span>
        <span>Đúng</span>
      </button>
      <button
        type="button"
        disabled={busy || !!current}
        onClick={() => send('down')}
        data-testid={`${testIdPrefix}-fb-down`}
        title="Câu trả lời này sai / thiếu"
        className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium border transition ${
          current === 'down'
            ? 'bg-rose-100 text-rose-800 border-rose-300 cursor-default'
            : current
              ? 'bg-white text-slate-300 border-slate-200 cursor-not-allowed'
              : 'bg-white text-slate-600 border-slate-200 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200'
        }`}
      >
        <span>👎</span>
        <span>Sai</span>
      </button>
    </>
  );
}

function BookmarkBtn({ questionId, isBookmarked, onChange, testId }) {
  const [busy, setBusy] = useState(false);
  async function toggle() {
    if (busy) return;
    setBusy(true);
    // Optimistic update — rollback nếu fail
    const next = !isBookmarked;
    onChange?.(questionId, next);
    try {
      if (next) {
        await api.bookmarkAdd(questionId);
        toast.success('Đã lưu bookmark 🔖');
      } else {
        await api.bookmarkRemove(questionId);
        toast('Đã bỏ bookmark', { icon: '🔖' });
      }
    } catch (err) {
      onChange?.(questionId, !next); // rollback
      const msg = err.data?.error || err.message || 'Bookmark thất bại';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      type="button"
      disabled={busy}
      onClick={toggle}
      data-testid={testId}
      title={isBookmarked ? 'Bỏ lưu — bấm để xoá khỏi danh sách' : 'Lưu để tra lại nhanh — xem tại trang "Đã lưu"'}
      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium border transition ${
        isBookmarked
          ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
          : 'bg-white text-slate-600 border-slate-200 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200'
      }`}
    >
      {/* M8.4-fix-C (2026-04-23): icon nhất quán — cùng 🔖 cho 2 trạng thái.
          Phân biệt qua BG (amber vs white) và opacity. Trước đây dùng 🏷️ vs 🔖
          khiến NV tưởng là 2 nút khác nhau → miss chức năng. */}
      <span className={isBookmarked ? '' : 'opacity-60 grayscale'}>🔖</span>
      <span>{isBookmarked ? 'Đã lưu' : 'Lưu'}</span>
    </button>
  );
}

function CopyBtn({ text, html, testId }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  async function onClick() {
    try {
      await copyToClipboard({ text, html });
      setCopied(true);
      toast.success('Đã copy — paste vào Word/Zalo giữ format, chat khác giữ plain');
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      toast.error(err.message || 'Copy thất bại — trình duyệt chặn');
    }
  }
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title="Copy — paste vào Word/Outlook giữ heading+bullet, paste vào chat thường ra markdown"
      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium border transition ${
        copied
          ? 'bg-sky-100 text-sky-800 border-sky-300'
          : 'bg-white text-slate-600 border-slate-200 hover:bg-sky-50 hover:text-sky-700 hover:border-sky-200'
      }`}
    >
      <span>{copied ? '✓' : '📋'}</span>
      <span>{copied ? 'Đã copy' : 'Copy'}</span>
    </button>
  );
}
